import styled from '@emotion/styled';
import { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import useUser, {
  isUserSubscribed,
} from '../../authentication/context/useUser';
import { useProject } from '../../project/context/useProject';

import { useTranslation } from 'react-i18next';
import ProjectActions from '../../project/ProjectActions';
import SubscriptionTeasing from '../../subscription/SubscriptionTeasing';
import GreenButton, { SubscriptionButton, WhiteButton, YellowButton } from '../../ui/Button';
import LanguageSwitcher from '../LanguageSwitcher';
import UserMenu from '../UserMenu';
import { ProjectBreadcrumb } from './ProjectBreadCrumb';

export const HeaderContainer = styled.header`
  height: 64px;
  width: 100%;
  position: sticky;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 24px;
  top: 0;
  background-color: var(--darkblue);
  z-index: 80;
`;

export const LogoWrapper = styled.img`
  max-width: 150px;
  max-height: 50%;
  cursor: pointer;
`;
export const ConsortiumWrapper = styled.img`
  max-width: 180px;
  max-height: 100%;
`;

export const HeaderProject = () => {
  const { projectId } = useParams();
  const { project } = useProject(projectId);
  const { user } = useUser();

  if (!project) return null;
  return (
    <HeaderContainer>
      <HeaderSection marginBetween="16px">
        {project && <ProjectBreadcrumb project={project!} />}
        <ProjectActions project={project} />
      </HeaderSection>
      <HeaderSection marginBetween="16px">
        <MiddleSection />
      </HeaderSection>
      <HeaderSection marginBetween="16px">
        <RightSection />
      </HeaderSection>
    </HeaderContainer>
  );
};

export const MiddleSection = () => {
  return (
    <>
      <ReportEdition />
      <LanguageSwitcher />
    </>
  );
};

export const RightSection = () => {
  const { user } = useUser();
  return (
    <>
      {!isUserSubscribed(user) && <SubButton />}
      <UserMenu />
    </>
  );
};

const SubButton = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  return (
    <>
      <SubscriptionButton onClick={() => navigate('/subscription')}>
        {t('common:subscription.headerButton').toLocaleUpperCase()}
      </SubscriptionButton>
    </>
  );
};

const ReportEdition = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { t } = useTranslation(['cnc-reports', 'annual-reports']);

  return (
    <>
      <YellowButton onClick={() => navigate('/cnc-reports')}>
        {t('cnc-reports:title').toLocaleUpperCase()}
      </YellowButton>
      {isUserSubscribed(user) ? (
        <GreenButton onClick={() => navigate('/annual-reports')}>
          {t('annual-reports:title').toLocaleUpperCase()}
        </GreenButton>
      ) : (
        <SubscriptionTeasing
          teasingButton={t(
            'common:subscription.annualReports.teasingButton',
          ).toLocaleUpperCase()}
          text={t('common:subscription.annualReports.modalText')}
          title={t('common:subscription.annualReports.modalTitle')}
          link={t('common:subscription.annualReports.knowMoreLink')}
        />
      )}
    </>
  );
};

export const HeaderProjects = () => {
  const navigate = useNavigate();
  return (
    <HeaderContainer>
      <HeaderSection marginBetween="16px">
        <LogoWrapper
          id="logoWrapper"
          src="/logo blanc.png"
          alt="logo"
          onClick={() => navigate('/')}
        />
      </HeaderSection>
      <HeaderSection marginBetween="16px" centered>
        <MiddleSection />
      </HeaderSection>
      <HeaderSection marginBetween="16px">
        <RightSection />
      </HeaderSection>
    </HeaderContainer>
  );
};

type HeaderSectionProps = {
  centered?: boolean;
  marginBetween?: string;
  children: ReactNode;
};

const HeaderSectionWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  position: ${(props: HeaderSectionProps) =>
    props.centered ? 'absolute' : ''};
  left: ${(props: HeaderSectionProps) => (props.centered ? '50%' : '')};
  transform: ${(props: HeaderSectionProps) =>
    props.centered ? 'translate(-50%, 0)' : ''};
  > *:not(:last-child) {
    margin-right: ${(props: HeaderSectionProps) =>
      props.marginBetween ? props.marginBetween : ''};
  }
`;

interface IconContainerProps {
  fill?: string;
}

export const IconContainer = styled.div<IconContainerProps>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  & svg {
    fill: ${({ fill }) => fill || 'currentColor'};
  }
`;

export const HeaderSection = ({
  marginBetween,
  centered,
  children,
}: HeaderSectionProps) => {
  return (
    <HeaderSectionWrapper marginBetween={marginBetween} centered={centered}>
      {children}
    </HeaderSectionWrapper>
  );
};

export const HeaderButton = WhiteButton;
